import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPADataTable from '../shared/SPADataTable';
import SPASectionHeading from '../shared/SPASectionHeading';
import type { LaunchesPayload } from './useAssignmentSyncState';

type Room = LaunchesPayload['classes'][number];

const date = (at: string) => new Date(at).toLocaleDateString();

const column = createColumnHelper<Room>();

// A class can open TCI from up to three courses; each course column lists them in the same order.
const PerCourse = ({ room, value }: { room: Room; value: (course: Room['courses'][number]) => string }) => (
  <>
    {room.courses.map(course => <span key={course.course_id} className="tw-block">{value(course)}</span>)}
  </>
);

const columns = [
  column.accessor('name', {
    cell: info => info.getValue() ?? `Class ${info.row.original.id}`,
    header: 'Class in TCI',
  }),
  column.accessor('students', { header: 'Students', meta: { align: 'right' } }),
  column.display({
    cell: ({ row: { original: room } }) => (room.courses.length === 0
      ? <span className="tw-text-spa-tertiary">No student has opened TCI from the LMS in the last year</span>
      : <PerCourse room={room} value={course => course.course_title ?? `LMS course ${course.course_id}`} />),
    header: 'LMS course it opens TCI from',
    id: 'course',
  }),
  column.display({
    cell: ({ row: { original: room } }) => (
      <PerCourse room={room} value={course => `${course.students} of ${room.students}`} />
    ),
    header: 'Students who opened it',
    id: 'opened',
    meta: { align: 'right' },
  }),
  column.display({
    cell: ({ row: { original: room } }) => <PerCourse room={room} value={course => date(course.last_at)} />,
    header: 'Last opened',
    id: 'last',
  }),
] as ColumnDef<Room, unknown>[];

// AIDEV-NOTE: Built from launch events, not LMS rosters, so it answers "which LMS course is this class"
// with no LTI key, an unreadable course, or an unlinked assignment — and it tells apart a teacher's
// same-named classes ("Social Studies 07" x3) by the course each one actually opens TCI from.
const ClassLaunches = ({ classes }: { classes: Room[] }) => (
  <div className="tw-mb-6">
    <SPASectionHeading title="Where its classes open TCI from" />
    <SPADataTable columns={columns} data={classes} defaultSort={[]} />
  </div>
);

export default ClassLaunches;
